/**
 * Genera un identificativo univoco in stile Base62 (29 caratteri + suffisso numerico),
 * nel formato richiesto esplicitamente da Nielsen per session/stream id.
 *
 * Modalità DETERMINISTICA (con `input`): l'ID è funzione pura dell'input.
 * È usata di proposito per lo `streamid` Audicom: passando lo STESSO `executionId`
 * sia all'SDK Video (`oce_acom_streamid` in ComScoreStreamingTagImpl) sia al Pairing
 * Ping (MegarideAudicomMapper.sendPairingPing), i due valori coincidono e permettono
 * la riconciliazione richiesta da Doc F. Un nuovo `executionId` (restart/reset = replay)
 * produce automaticamente un nuovo streamid, come da spec. Inizialmente si passava
 * l'executionId grezzo, ma Nielsen ha richiesto questo formato: da qui il determinismo.
 *
 * Modalità CASUALE (senza `input`): ID random con suffisso timestamp; usata per il
 * sessionid, dove non serve accoppiamento tra sottosistemi.
 *
 * @param input - (Opzionale) se fornito (es. executionId) genera un ID deterministico;
 *                se omesso genera un ID casuale basato sul tempo corrente.
 * @returns Stringa di 29 caratteri Base62 + suffisso numerico.
 */

export function generateUID(input?: string): string {
    const CHARSET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    const PAYLOAD_LENGTH = 29;

    // --- Logica Deterministica (Hashing FNV-1a) ---
    const getHash = (str: string) => {
        let hash = 2166136261;
        for (let i = 0; i < str.length; i++) {
            hash ^= str.charCodeAt(i);
            hash = Math.imul(hash, 16777619);
        }
        return Math.abs(hash);
    };

    // --- Logica Casuale (Originale buildGUID) ---
    const getRandomIndex = () => {
        let r;
        do {
            r = Math.floor(Math.random() * (16 * 62));
        } while (r > 61);
        return r;
    };

    let uid = '';
    let currentHash = input ? getHash(input) : 0;

    for (let i = 0; i < PAYLOAD_LENGTH; i++) {
        if (input) {
            // Generazione deterministica basata sull'input
            currentHash = Math.imul(currentHash ^ i, 16777619);
            uid += CHARSET.charAt(Math.abs(currentHash) % 62);
        } else {
            // Generazione casuale originale
            uid += CHARSET.charAt(getRandomIndex());
        }
    }

    // Aggiunta del suffisso numerico
    if (input) {
        // Suffisso derivato dall'input (rimane costante per lo stesso input)
        const deterministicSuffix = Math.abs(Math.imul(currentHash, 16777619)) % 10000000000;
        uid += deterministicSuffix.toString().padStart(10, '0');
    } else {
        // Suffisso basato sul tempo reale (comportamento originale)
        uid += Math.floor(new Date().getTime() / 1000);
    }

    return uid;
}